import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import * as React from 'react';

import type { ShouldShowProps } from '../../types';
import { LinkEditBlock } from '../link/link-edit-block';
import { LinkPopoverBlock } from '../link/link-popover-block';

interface LinkBubbleMenuProps {
    editor: Editor;
}

interface LinkAttributes {
    href: string;
    target: string;
}

export const LinkBubbleMenu: React.FC<LinkBubbleMenuProps> = ({ editor }) => {
    const [showEdit, setShowEdit] = React.useState(false);
    const [linkAttrs, setLinkAttrs] = React.useState<LinkAttributes>({
        href: '',
        target: '',
    });
    const [selectedText, setSelectedText] = React.useState('');

    const updateLinkState = React.useCallback(() => {
        const { from, to } = editor.state.selection;
        const { href, target } = editor.getAttributes('link');
        const text = editor.state.doc.textBetween(from, to, ' ');

        setLinkAttrs({ href, target });
        setSelectedText(text);
    }, [editor]);

    const shouldShow = React.useCallback(
        ({ editor: editorInstance, from, to }: ShouldShowProps) => {
            if (from === to) {
                return false;
            }
            const { href } = editorInstance.getAttributes('link');

            if (!editorInstance.isActive('link') || !editorInstance.isEditable) {
                return false;
            }

            if (href) {
                updateLinkState();
                return true;
            }
            return false;
        },
        [updateLinkState],
    );

    const handleEdit = React.useCallback(() => {
        setShowEdit(true);
    }, []);

    const onSetLink = React.useCallback(
        (url: string, text?: string, openInNewTab?: boolean) => {
            editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .insertContent({
                    type: 'text',
                    text: text || url,
                    marks: [
                        {
                            type: 'link',
                            attrs: {
                                href: url,
                                target: openInNewTab ? '_blank' : '',
                            },
                        },
                    ],
                })
                .setLink({ href: url, target: openInNewTab ? '_blank' : '' })
                .run();
            setShowEdit(false);
            updateLinkState();
        },
        [editor, updateLinkState],
    );

    const onUnsetLink = React.useCallback(() => {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        setShowEdit(false);
        updateLinkState();
    }, [editor, updateLinkState]);

    // Must be reference-stable: since @tiptap/react 3.27 BubbleMenu dispatches an
    // "updateOptions" transaction whenever the options identity changes, and an inline
    // object here creates a dispatch → re-render → dispatch infinite loop.
    const bubbleMenuOptions = React.useMemo(
        () => ({
            placement: 'bottom-start' as const,
            onHide: () => setShowEdit(false),
        }),
        [],
    );

    return (
        <BubbleMenu editor={editor} shouldShow={shouldShow} options={bubbleMenuOptions}>
            {showEdit ? (
                <LinkEditBlock
                    defaultUrl={linkAttrs.href}
                    defaultText={selectedText}
                    defaultIsNewTab={linkAttrs.target === '_blank'}
                    onSave={onSetLink}
                    className="bg-popover text-popover-foreground w-full min-w-80 rounded-md border p-4 shadow-md outline-hidden"
                />
            ) : (
                <LinkPopoverBlock onClear={onUnsetLink} url={linkAttrs.href} onEdit={handleEdit} />
            )}
        </BubbleMenu>
    );
};
